import { useEffect, useRef, useState } from 'react';

import { motion } from 'framer-motion';

interface TextSegment {
  text: string;
  isStrong: boolean;
}

interface TypingTextProps {
  fullText: string;
  delay?: number;
  speed?: number;
  onComplete?: () => void;
}

const parseTextSegments = (text: string): TextSegment[] => {
  const segments: TextSegment[] = [];
  let currentIndex = 0;

  while (currentIndex < text.length) {
    const strongStart = text.indexOf('<strong>', currentIndex);

    if (strongStart === -1) {
      if (currentIndex < text.length) {
        segments.push({ text: text.slice(currentIndex), isStrong: false });
      }
      break;
    }

    if (strongStart > currentIndex) {
      segments.push({
        text: text.slice(currentIndex, strongStart),
        isStrong: false
      });
    }

    const afterStrongTag = text.slice(strongStart + 8);
    const strongEnd = afterStrongTag.indexOf('</strong>');

    if (strongEnd !== -1) {
      const strongContent = afterStrongTag.slice(0, strongEnd);

      segments.push({
        text: strongContent,
        isStrong: true
      });
      currentIndex = strongStart + 8 + strongEnd + 9;
    } else {
      break;
    }
  }

  return segments;
};

const TypingText: React.FC<TypingTextProps> = ({
  fullText,
  delay = 0,
  speed = 3,
  onComplete
}) => {
  const [currentSegmentIndex, setCurrentSegmentIndex] = useState(0);
  const [currentSegmentText, setCurrentSegmentText] = useState('');
  const hasStartedRef = useRef(false);
  const segmentsRef = useRef<TextSegment[]>([]);
  const textRef = useRef<HTMLSpanElement>(null);
  const onCompleteRef = useRef(onComplete);

  // Keep onComplete ref up to date without triggering re-renders
  useEffect(() => {
    onCompleteRef.current = onComplete;
  }, [onComplete]);

  useEffect(() => {
    segmentsRef.current = parseTextSegments(fullText);
    // Reset state when text changes
    hasStartedRef.current = false;
    setCurrentSegmentIndex(0);
    setCurrentSegmentText('');
  }, [fullText]);

  useEffect(() => {
    if (segmentsRef.current.length === 0 || hasStartedRef.current) return;

    hasStartedRef.current = true;

    const timer = setTimeout(() => {
      let segmentIndex = 0;
      let charIndex = 0;

      const typeNextChar = () => {
        if (segmentIndex >= segmentsRef.current.length) {
          onCompleteRef.current?.();

          return;
        }

        const segment = segmentsRef.current[segmentIndex];

        if (charIndex < segment.text.length) {
          setCurrentSegmentText(segment.text.slice(0, charIndex + 1));
          setCurrentSegmentIndex(segmentIndex);
          charIndex++;
          setTimeout(typeNextChar, speed);
        } else {
          setCurrentSegmentText(segment.text);
          segmentIndex++;
          charIndex = 0;
          setTimeout(typeNextChar, speed);
        }
      };

      typeNextChar();
    }, delay);

    return () => clearTimeout(timer);
  }, [fullText, delay, speed]);

  const renderContent = () => {
    const parts: (string | JSX.Element)[] = [];
    let key = 0;

    segmentsRef.current.forEach((segment, index) => {
      if (index < currentSegmentIndex) {
        // Completed segments - render with full opacity
        if (segment.isStrong) {
          parts.push(
            <strong key={key++} className="text-primary">
              {segment.text.split('').map((char, charIndex) => (
                <motion.span
                  key={`${key}-${charIndex}`}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 2 }}
                  style={{ display: 'inline' }}
                >
                  {char}
                </motion.span>
              ))}
            </strong>
          );
        } else {
          segment.text.split('').forEach((char) => {
            parts.push(
              <motion.span
                key={key++}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 2 }}
                style={{ display: 'inline' }}
              >
                {char}
              </motion.span>
            );
          });
        }
      } else if (index === currentSegmentIndex) {
        // Current segment being typed - animate each character as it appears
        if (segment.isStrong) {
          parts.push(
            <strong key={key++} className="text-primary">
              {currentSegmentText.split('').map((char, charIndex) => (
                <motion.span
                  key={`${key}-${charIndex}`}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 2 }}
                  style={{ display: 'inline' }}
                >
                  {char}
                </motion.span>
              ))}
            </strong>
          );
        } else {
          currentSegmentText.split('').forEach((char) => {
            parts.push(
              <motion.span
                key={key++}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 2 }}
                style={{ display: 'inline' }}
              >
                {char}
              </motion.span>
            );
          });
        }
      }
    });

    return parts.length > 0 ? parts : null;
  };

  return (
    <motion.span
      translate="no"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3 }}
      style={{
        position: 'relative',
        display: 'inline'
      }}
    >
      <span ref={textRef} translate="no" style={{ display: 'inline' }}>
        {renderContent()}
      </span>
    </motion.span>
  );
};

export default TypingText;
